import * as R from 'ramda'
import { ComponentType } from 'react'
import { connect as reduxConnect } from 'react-redux'
import { firestoreConnect, isLoaded, withFirestore } from 'react-redux-firebase'
import { compose, withProps } from 'recompose'

import { Flight } from '../schemas/Flight'
import { PlayerId, TournamentPlayer } from '../schemas/Player'
import { Playground } from '../schemas/Playground'
import { TenantId } from '../schemas/Tenant'
import { Tournament, TournamentId } from '../schemas/Tournament'

import { followingFlightsWithDecrementedNumber } from './flights'
import { reprocessTournamentPlayers } from './players'
import { categoryResults } from './results'

export const fsCollections = {
  tournaments: (tenant) => `/tenants/${tenant}/tournaments`,
  tournamentPlayers: (tenant, tournamentId) =>
    `/tenants/${tenant}/tournaments/${tournamentId}/players`,
  tournamentFlights: (tenant, tournamentId) =>
    `/tenants/${tenant}/tournaments/${tournamentId}/flights`,
  tournamentResults: (tenant, tournamentId) =>
    `/tenants/${tenant}/tournaments/${tournamentId}/results`,
  tournamentCombinedResults: (tenant, tournamentId) =>
    `/tenants/${tenant}/tournaments/${tournamentId}/combinedResults`,
  tournamentCategories: (tenant, tournamentId) =>
    `/tenants/${tenant}/tournaments/${tournamentId}/categories`,
  tournamentTeams: (tenant, tournamentId) => `/tenants/${tenant}/tournaments/${tournamentId}/teams`,
  globalPlayers: (tenant) => `/tenants/${tenant}/players`,
  globalCategories: (tenant) => `tenants/${tenant}/categories`,
  pairings: (tenant, tournamentId) => `/tenants/${tenant}/tournaments/${tournamentId}/pairings`,
  championships: (tenant) => `/tenants/${tenant}/championships`,
  championshipCategories: (tenant, championshipId) =>
    `/tenants/${tenant}/championships/${championshipId}/categories`,
  championshipResults: (tenant, championshipId) =>
    `/tenants/${tenant}/championships/${championshipId}/results`,
  globalTeams: (tenant) => `/tenants/${tenant}/teams`,
}

export const withoutUndefined = R.filter(R.complement(R.isNil))

export const makeInBatchWithFirebase = ({ firebase }) => (
  operations,
) /* (firebase, batch) => void */ => {
  const firestore = firebase.firestore()
  const batch = firestore.batch()
  operations(firestore, batch)

  return batch.commit()
}

export const saveTournamentPlayers = ({ tenant, tournamentId }) => (firestore, batch) => (
  tournamentPlayers,
) => {
  const collection = firestore.collection(fsCollections.tournamentPlayers(tenant, tournamentId))

  tournamentPlayers.forEach((tournamentPlayer) =>
    batch.set(collection.doc(tournamentPlayer.id), withoutUndefined(tournamentPlayer)),
  )
}

// adds operations to batch via side effect
export const batchReprocessTournamentPlayers = ({
  tenant,
  tournamentId,
  tournament,
  globalPlayers,
  tournamentCategories,
  tournamentPlayers,
}) => (firestore, batch) => {
  const updatedTournamentPlayers = reprocessTournamentPlayers(
    tournament.dateFrom,
    tournamentCategories,
    globalPlayers,
  )(tournamentPlayers)
  saveTournamentPlayers({ tenant, tournamentId })(firestore, batch)(updatedTournamentPlayers)
}
export const saveFlights = ({ tenant, tournamentId, flights }) => (firestore, batch) => (
  ...modifiedFlights
) => {
  const collection = firestore.collection(fsCollections.tournamentFlights(tenant, tournamentId))

  // save each flight
  modifiedFlights.forEach((flight) => {
    if (flight.players.length > 0) {
      // update if flight has id specified, insert otherwise
      batch.set(flight.id ? collection.doc(flight.id) : collection.doc(), withoutUndefined(flight))
    } else {
      // delete empty flight
      batch.delete(collection.doc(flight.id))

      // decrement number of following flights
      followingFlightsWithDecrementedNumber(flight)(flights).forEach((followingFlight) => {
        batch.set(collection.doc(followingFlight.id), followingFlight)
      })
    }
  })
}

type WithFlightsDataOuterProps = {
  tenant: TenantId
  tournamentId: TournamentId
  playground: Playground
}

export type WithFlightsDataInnerProps = WithFlightsDataOuterProps & {
  tournament?: Tournament
  flights?: Flight[]
  flightsPlayers?: {
    [flightID: string]: TournamentPlayer[]
  }
  flightsDataLoaded: boolean
  playerIdsByPositionInMain?: PlayerId[]
}

export const dataFromOrdered = <T extends { id: string }>(
  ordered: T[] | undefined,
): { [key: string]: T } | undefined =>
  R.isNil(ordered)
    ? undefined
    : ordered.reduce((acc, t) => ({ ...acc, [t.id]: t }), {} as { [key: string]: T })

export const findInOrdered = <T extends { id: string }>(
  ordered: T[] | undefined,
  id: string,
): T | undefined => (R.isNil(ordered) ? undefined : ordered.find((t) => t.id === id))

export function withFlightsData<P>(component: ComponentType<P & WithFlightsDataInnerProps>) {
  return compose<P & WithFlightsDataInnerProps, P & WithFlightsDataOuterProps>(
    withFirestore,
    firestoreConnect<FIXME>(({ tenant, tournamentId }) => [
      { collection: fsCollections.tournaments(tenant), doc: tournamentId },
      { collection: fsCollections.tournamentFlights(tenant, tournamentId) },
      { collection: fsCollections.tournamentPlayers(tenant, tournamentId) },
      { collection: fsCollections.tournamentCombinedResults(tenant, tournamentId) },
      { collection: fsCollections.tournamentCategories(tenant, tournamentId) },
    ]),
    reduxConnect(({ firestore }: FIXME, { tenant, tournamentId }: FIXME) => ({
      tournament: findInOrdered(firestore.ordered[fsCollections.tournaments(tenant)], tournamentId),
      flights: firestore.ordered[fsCollections.tournamentFlights(tenant, tournamentId)],
      playersMap: dataFromOrdered(
        firestore.ordered[fsCollections.tournamentPlayers(tenant, tournamentId)],
      ),
      tournamentPlayers: firestore.ordered[fsCollections.tournamentPlayers(tenant, tournamentId)],
      tournamentPlayersMap: dataFromOrdered(
        firestore.ordered[fsCollections.tournamentPlayers(tenant, tournamentId)],
      ),
      tournamentCombinedResults:
        firestore.ordered[fsCollections.tournamentCombinedResults(tenant, tournamentId)],
      categories: firestore.ordered[fsCollections.tournamentCategories(tenant, tournamentId)],
    })),
    withProps(({ flights, playersMap }) => ({
      flightsPlayers: isLoaded(flights, playersMap)
        ? flights.reduce(
            (acc, flight) => ({
              ...acc,
              [flight.id]: flight.players.map((playerId: PlayerId) =>
                R.assoc('id', playerId, playersMap[playerId]),
              ),
            }),
            {},
          )
        : undefined,
    })),
    withProps(({ categories }) => ({
      mainCategory: isLoaded(categories) ? categories.find((category) => category.main) : undefined,
    })),
    withProps(
      ({ tournament, flights, tournamentCombinedResults, tournamentPlayersMap, mainCategory }) => ({
        playerIdsByPositionInMain:
          isLoaded(tournament, flights, tournamentCombinedResults, tournamentPlayersMap) &&
          mainCategory
            ? categoryResults(
                tournament,
                mainCategory,
                flights,
                tournamentCombinedResults,
                tournamentPlayersMap,
              ).map((result: FIXME) => result.id)
            : undefined,
      }),
    ),
    withProps(({ tournament, flights, flightsPlayers }) => ({
      flightsDataLoaded: isLoaded(tournament, flights) && !R.isNil(flightsPlayers),
    })),
  )(component)
}



// WEBPACK FOOTER //
// ./src/helpers/firestore.ts